import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Button, Icon, Stack, Text } from '@/design-system'
import { FlowLayout } from '@/app/layouts/FlowLayout'
import { cn } from '@/lib/cn'
import { STEP_NUMBER, TOTAL_STEPS, useStep } from './progress'
import styles from './Onboarding.module.css'

type CheckState = 'todo' | 'working' | 'done'

const COUNTRIES = ['United States', 'Mexico', 'Colombia', 'Brazil', 'Bolivia', 'Spain', 'United Kingdom']

/** 09 · Step 3 of 5 — the regulated part, kept to what is actually required. */
export function OnboardingIdentity() {
  useStep('identity')
  const navigate = useNavigate()
  const [country, setCountry] = useState('United States')
  const [idDoc, setIdDoc] = useState<CheckState>('todo')
  const [selfie, setSelfie] = useState<CheckState>('todo')

  // Stands in for the verification partner's SDK, which answers asynchronously.
  const run = (set: (s: CheckState) => void) => {
    set('working')
    window.setTimeout(() => set('done'), 900)
  }

  const done = idDoc === 'done' && selfie === 'done'

  const rows = [
    {
      icon: 'badge',
      title: 'Photo ID',
      body: idDoc === 'done' ? 'Passport · checked' : 'Passport, national ID or driving licence',
      state: idDoc,
      action: 'Upload',
      onClick: () => run(setIdDoc),
    },
    {
      icon: 'face',
      title: 'Selfie check',
      body: selfie === 'done' ? 'Matched to your ID' : 'Takes about 20 seconds',
      state: selfie,
      action: 'Start',
      onClick: () => run(setSelfie),
    },
  ]

  return (
    <FlowLayout
      step={STEP_NUMBER.identity}
      totalSteps={TOTAL_STEPS}
      back="/onboarding/verify/code"
      footer={
        <Button
          size="lg"
          block
          iconEnd="arrow_forward"
          disabled={!done}
          onClick={() => navigate('/onboarding/accounts')}
        >
          {done ? 'Continue' : 'Verify your identity to continue'}
        </Button>
      }
    >
      <Stack gap={6}>
        <Stack gap={2}>
          <Text as="h1" variant="title" weight="bold">
            Verify your identity
          </Text>
          <Text variant="body" tone="secondary">
            Required to open regulated accounts. Most people finish in under two minutes.
          </Text>
        </Stack>

        <label className={styles.row}>
          <Icon name="public" size={20} />
          <Stack grow gap={0}>
            <Text variant="caption" tone="secondary">
              Country of residence
            </Text>
            <select
              value={country}
              onChange={(event) => setCountry(event.target.value)}
              style={{ font: 'inherit', fontWeight: 600, border: 0, background: 'none', padding: 0 }}
            >
              {COUNTRIES.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </Stack>
        </label>

        <Stack gap={2}>
          {rows.map((row) => (
            <div key={row.title} className={cn(styles.row, row.state === 'done' && styles.rowDone)}>
              <Icon name={row.state === 'done' ? 'check_circle' : row.icon} size={20} filled={row.state === 'done'} />
              <Stack grow gap={0}>
                <Text variant="caption" weight="semibold">
                  {row.title}
                </Text>
                <Text variant="caption" tone="secondary">
                  {row.body}
                </Text>
              </Stack>
              {row.state !== 'done' && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={row.state === 'working'}
                  onClick={row.onClick}
                >
                  {row.state === 'working' ? 'Checking…' : row.action}
                </Button>
              )}
            </div>
          ))}
          <div className={cn(styles.row, done && styles.rowDone)}>
            <Icon name="home" size={20} />
            <Stack grow gap={0}>
              <Text variant="caption" weight="semibold">
                Home address
              </Text>
              <Text variant="caption" tone="secondary">
                {done ? 'Filled in from your ID — check it on the next screen' : 'Filled in from your ID'}
              </Text>
            </Stack>
          </div>
        </Stack>

        <div className={styles.stats}>
          {[
            ['2 min', 'average'],
            ['195', 'countries'],
            ['Instant', 'in most cases'],
          ].map(([value, label]) => (
            <div key={label} className={styles.stat}>
              <Text variant="subtitle" weight="bold">
                {value}
              </Text>
              <Text variant="caption" tone="secondary">
                {label}
              </Text>
            </div>
          ))}
        </div>

        <Stack direction="row" align="start" gap={3}>
          <Icon name="lock" size={18} />
          <Text variant="caption" tone="secondary">
            Your documents are encrypted and held by our licensed verification partner. They are
            never shared with anyone else.
          </Text>
        </Stack>
      </Stack>
    </FlowLayout>
  )
}
